import {
  Alert,
  Box,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Loading } from 'framework/ui/mui-builder';
import { useCoreServiceClient } from '../../api/coreServiceClient';
import useAsyncData from '../../hooks/useAsyncData';
import EmptyState from '../../components/EmptyState';
import ErrorNotice from '../../components/ErrorNotice';
import SectionLabel from '../../components/SectionLabel';
import StatTile from '../../components/StatTile';
import { formatNumber } from '../../utils/format';

const joinCounts = (counts: Record<string, number>, separator: string) =>
  Object.entries(counts)
    .map(([key, value]) => `${key}${separator}${formatNumber(value)}`)
    .join(' · ');

const AllocationTab = () => {
  const client = useCoreServiceClient();
  const summary = useAsyncData(() => client.getAllocationSummary(), [client]);
  const data = summary.data;

  if (summary.error && !data) {
    return <ErrorNotice error={summary.error} onRetry={summary.reload} />;
  }

  if (!data) {
    return <Loading isInitialPosition />;
  }

  const percent = data.tickets ? Math.round((100 * data.allocated) / data.tickets) : 0;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
        Tickets are placed under finance cost centers (business functions) and their services by the davinci/finance
        mapping rules: one-stop &gt; issue category / project &gt; process.
      </Typography>

      <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ marginBottom: 1.5 }}>
        <StatTile value={`${percent}%`} label="tickets allocated" />
        <StatTile value={formatNumber(data.allocated)} label={`of ${formatNumber(data.tickets)}`} />
        <StatTile value={formatNumber(data.costCenters)} label="cost centers" />
        <StatTile value={formatNumber(data.services)} label="services" />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
        {joinCounts(data.byStatus, ': ')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Rules: {data.rulesSource} ({joinCounts(data.rules, ' ')})
      </Typography>
      {!data.processEngineConnected ? (
        <Alert severity="warning" sx={{ marginTop: 1 }}>
          business-engine/Camunda DBs not connected: process and one-stop rules cannot match, results are partial
        </Alert>
      ) : null}
      {data.processEngineError ? (
        <Alert severity="error" sx={{ marginTop: 1 }}>
          {data.processEngineError}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <SectionLabel>By service</SectionLabel>
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell>Cost center</TableCell>
                    <TableCell align="right">Tickets</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.byService.map(service => (
                    <TableRow key={service.serviceId} hover>
                      <TableCell>{service.service || service.serviceId}</TableCell>
                      <TableCell>{service.costCenter}</TableCell>
                      <TableCell align="right">{formatNumber(service.tickets)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {data.byService.length ? null : <EmptyState>No allocated services</EmptyState>}
          </Paper>
        </Box>

        <Box sx={{ flex: 1, minWidth: 280 }}>
          <SectionLabel>Top unallocated projects</SectionLabel>
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Project</TableCell>
                    <TableCell align="right">Tickets</TableCell>
                    <TableCell>Why</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.topUnallocatedProjects.map(project => (
                    <TableRow key={project.projectId} hover>
                      <TableCell>{project.project || project.projectId}</TableCell>
                      <TableCell align="right">{formatNumber(project.tickets)}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{joinCounts(project.statuses, ' ')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {data.topUnallocatedProjects.length ? null : <EmptyState>Nothing unallocated</EmptyState>}
          </Paper>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" component="div" sx={{ marginTop: 1.5 }}>
        <Link component="button" type="button" onClick={summary.reload}>
          Refresh
        </Link>
      </Typography>
    </Box>
  );
};

export default AllocationTab;
